import { formatNumber } from "@angular/common";
import { Injectable, inject } from "@angular/core";
import { TranslateService } from "@ngx-translate/core";
import { ChartConfiguration, ChartData, ChartDataset } from "chart.js";
import { IHandelssortiment } from "~app/features/planera/model/IHandelssortiment";
import { IHandelssortimentsgrupp } from "~app/features/planera/model/IHandelssortimentsgrupp";
import { ProduktionsmalPlanMode } from "~app/features/planera/model/ProduktionsmalPlanMode";
import { PlaneraBridgeEvents, PlaneraEventService } from "../../services/planera-event.service";
import { ProduktionsmalPlanArbetsorderSearch } from "../produktionsmal-plan-arbetsordersearch.model";
interface IChartData extends ChartData {
    lessThanZeroLabels: string[];
    labels: string[];
    relaterade: INamedLabelList[];
}

export interface INamedLabelList {
    name: string;
    children: string[];
}

export enum ChartMode {

    Handelssortimentsgrupper,

    Handelssortimentsgrupp
}

export enum TooltipMode {

    Mal = 0,

    Klara = 1,

    Produktsatt = 2,

    Periodsatt = 3,

    Leveransvirke = 4
}

@Injectable({
    providedIn: "root"
})

export class ProduktionsmalPlanChartService {
    private readonly $translate = inject(TranslateService);
    private readonly planeraEventService = inject(PlaneraEventService);

    private readonly extraHeight = 50;
    private readonly minHeight = 180;
    private readonly defaultBarHeight = 45;
    private readonly paddingHeight = 30;
    private readonly stackName = "Klara-Produktsatt-Periodsatt";
    private readonly colors = {
        mal: "#F68D38",
        produktsatt: "#3A3A41",
        periodsatt: "#5AA0FE",
        leveransvirke: "#FF00FF",
        klara: "#0c9427"
    };
    private readonly warningGlyph = "\u26A0";

    readonly searchForArbetsorder = (handelssortimentId: number[], planeringsstatus: string, produktionsmalPlan, produktgruppIds?: number[]) => {
        this.searchForArbetsorderCustom({
            handelssortimentId: handelssortimentId,
            planeringsstatus: planeringsstatus,
            period: produktionsmalPlan.period,
            poId: produktionsmalPlan.produktionsomradeId,
            produktgruppIds: produktgruppIds
        });
    };

    readonly searchForArbetsorderCustom = (searchObj: ProduktionsmalPlanArbetsorderSearch) => {
        this.planeraEventService.emit(PlaneraBridgeEvents.searchArbetsorderViaStaplar, searchObj);
    };

    readonly getHandelssortimentByKod = (handelssortimentsgruppKod: string, handelssortimentsgrupper): IHandelssortimentsgrupp => {
        const handelssortimentsgrupperFiltered = handelssortimentsgrupper.filter(a => a.kod === handelssortimentsgruppKod);
        if (handelssortimentsgrupperFiltered && handelssortimentsgrupperFiltered.length === 1) {
            return handelssortimentsgrupperFiltered[0];
        }

        return null;
    };

    getChartHeight(labelsCount: number, produktionsmalPlanMode) {
        const suggestedHeight = (this.extraHeight + labelsCount * this.defaultBarHeight);
        if (suggestedHeight > this.minHeight) {
            return suggestedHeight;
        } else {
            const newHeight = suggestedHeight + this.paddingHeight + (produktionsmalPlanMode === ProduktionsmalPlanMode.detail ? 70 : 0);
            if (newHeight > this.minHeight) {
                return newHeight;
            }
            if (labelsCount > 0) {
                return this.minHeight;
            }
            return newHeight;
        }
    }

    readonly markNegativeGoals = (labels, negativeGoals: string[]): void => {
        for (let i = 0; i < labels.length; i++) {
            const label = labels[i];
            if (negativeGoals.indexOf(label) > -1) {
                labels[i] = `${this.warningGlyph} ${label}`;
            }
        }
    };

    readonly getNamedLabellist = (handelssortiment: IHandelssortiment) => ({
        name: handelssortiment.namn,
        children: handelssortiment.relateradeHandelssortiment.map(rhs => rhs.namn)
    });

    readonly getDataForHandelssortimentsgrupper = (handelssortimentsgrupper: IHandelssortimentsgrupp[]): IChartData => {
        const labels = handelssortimentsgrupper.map(a => a.namn);

        const malkvantitet = handelssortimentsgrupper.map(a => a.malKvantitet);
        const produktsattTotalKvantitet = handelssortimentsgrupper.map(a => this.calculateProduktsattSum(a));
        const periodsattTotalKvantitet = handelssortimentsgrupper.map(a => this.calculatePeriodsattSum(a));
        const leveransvirkeTotalKvantitet = handelssortimentsgrupper.map(a => this.calculateLeveransvirkeSum(a));
        const klaraTotalKvantitet = handelssortimentsgrupper.map(a => this.calculateKlaraSum(a));
        return {
            labels: labels,
            lessThanZeroLabels: this.gatherAndZeroNegativeGoals(malkvantitet, labels),
            datasets: this.createDatasets(malkvantitet,
                produktsattTotalKvantitet,
                periodsattTotalKvantitet,
                leveransvirkeTotalKvantitet,
                klaraTotalKvantitet),
            relaterade: null,
            handelssortimentgrupper: handelssortimentsgrupper,
            selectedHandelssortimentgrupp: null
        };
    };

    readonly getDataForHandelssortimentsgrupp = (handelssortimentsgrupp: IHandelssortimentsgrupp, produktionsmalPlanTopModeEnabled: boolean): IChartData => {
        const handelssortimentsgruppHandelssortiment = handelssortimentsgrupp.handelssortiment;
        const labels = handelssortimentsgruppHandelssortiment.map(a => a.namn);

        const malkvantitet = handelssortimentsgruppHandelssortiment.map(a => a.malKvantitet);
        const produktsattTotalKvantitet = handelssortimentsgruppHandelssortiment.map(a => this.calculateProduktsattSum(a));
        const leveransvirkeTotalKvantitet = handelssortimentsgruppHandelssortiment.map(a => this.calculateLeveransvirkeSum(a));
        const klaraTotalKvantitet = handelssortimentsgruppHandelssortiment.map(a => this.calculateKlaraSum(a));
        const periodsattKvantitet = [...Array(handelssortimentsgruppHandelssortiment.length)].map(() => 0);
        const relaterade: INamedLabelList[] = handelssortimentsgruppHandelssortiment.map(a => this.getNamedLabellist(a));

        if (produktionsmalPlanTopModeEnabled) {
            // En tom rad utan några kvantiteter
            labels.unshift("");
            malkvantitet.unshift(0);
            produktsattTotalKvantitet.unshift(0);
            periodsattKvantitet.unshift(0);
            leveransvirkeTotalKvantitet.unshift(0);
            klaraTotalKvantitet.unshift(0);

            // Handelssortimentsgruppens namn och kvantiteter
            labels.unshift(handelssortimentsgrupp.namn);
            malkvantitet.unshift(handelssortimentsgrupp.malKvantitet);
            produktsattTotalKvantitet.unshift(this.calculateProduktsattSum(handelssortimentsgrupp));
            periodsattKvantitet.unshift(this.calculatePeriodsattSum(handelssortimentsgrupp));
            leveransvirkeTotalKvantitet.unshift(this.calculateLeveransvirkeSum(handelssortimentsgrupp));
            klaraTotalKvantitet.unshift(this.calculateKlaraSum(handelssortimentsgrupp));
        }

        return {
            labels: labels,
            lessThanZeroLabels: this.gatherAndZeroNegativeGoals(malkvantitet, labels),
            datasets: this.createDatasets(malkvantitet, produktsattTotalKvantitet, periodsattKvantitet, leveransvirkeTotalKvantitet, klaraTotalKvantitet),
            relaterade: relaterade,
            handelssortimentgrupper: null,
            selectedHandelssortimentgrupp: handelssortimentsgrupp
        };
    };

    readonly getSelectedHandelssortimentGrupp = (namn: string, handelssortimentsgrupper: IHandelssortimentsgrupp[]) => handelssortimentsgrupper.find(p => p.namn === namn);

    // initializeChart
    readonly getChartConfiguration = (data: IChartData, plugins: any, scales, planMode, chartMode): ChartConfiguration => {
        const rgbaBlackOpacity = "rgba(0,0,0,1)";
        const suggestedHeight = (this.extraHeight + data.labels.length * this.defaultBarHeight);
        return {
            type: "bar",
            data: data,
            options: {
                indexAxis: "y",
                maintainAspectRatio: false,
                responsive: true,
                scales: scales,
                layout: {
                    padding: {
                        left: 0,
                        right: 0,
                        top: 0,
                        // eslint-disable-next-line sonarjs/no-nested-conditional
                        bottom: (suggestedHeight > this.minHeight ? 0 : this.paddingHeight + (planMode === ProduktionsmalPlanMode.detail ? 50 : 0))
                    }
                },
                plugins: {
                    tooltip: {
                        position: "nearest",
                        displayColors: true,
                        backgroundColor: "rgba(255,255,255,1)",
                        borderColor: rgbaBlackOpacity,
                        cornerRadius: 3,
                        padding: 12,
                        footerColor: rgbaBlackOpacity,
                        footerFont: {
                            weight: "bold"
                        },
                        footerMarginTop: 10,
                        borderWidth: 1,
                        bodyFont: {
                            weight: "normal"
                        },
                        intersect: true,
                        mode: "nearest",
                        callbacks: {
                            title: () => "",
                            labelColor: tooltipItem => {
                                const color = this.getCustomLabelColor(tooltipItem.datasetIndex);
                                return {
                                    borderColor: color,
                                    backgroundColor: color
                                };
                            },
                            labelTextColor: () => rgbaBlackOpacity,
                            label: tooltipItem => this.getCustomTooltipLabel(
                                tooltipItem.datasetIndex, tooltipItem.dataIndex, tooltipItem.label, chartMode, planMode, data.relaterade),

                            footer: tooltipItem => {
                                const tooltipItemZero = tooltipItem[0];
                                return this.getCustomTooltipFooter(
                                    tooltipItemZero.datasetIndex,
                                    tooltipItemZero.dataIndex,
                                    chartMode,
                                    planMode,
                                    data.selectedHandelssortimentgrupp,
                                    data.handelssortimentgrupper);
                            }
                        },
                        enabled: true
                    },
                    legend: {
                        display: false
                    }
                }
            },
            plugins: plugins
        };

    };

    private readonly gatherAndZeroNegativeGoals = (malkvantitet: any[], labels: string[]): string[] => {
        const lessThanZeroLabels: string[] = [];

        for (let i = 0; i < malkvantitet.length; i++) {
            if (malkvantitet[i] < 0) {
                lessThanZeroLabels.push(labels[i]);
                malkvantitet[i] = 0;
            }
        }
        return lessThanZeroLabels;
    };

    private readonly calculateProduktsattSum = (handelssortimentsgrupp: IHandelssortimentsgrupp | IHandelssortiment): number =>
        handelssortimentsgrupp.produktsattManadKvantitet + handelssortimentsgrupp.produktsattMaskinlagKvantitet;

    private readonly calculatePeriodsattSum = (handelssortimentsgrupp: IHandelssortimentsgrupp): number =>
        handelssortimentsgrupp.periodsattManadKvantitet + handelssortimentsgrupp.periodsattMaskinlagKvantitet;

    private readonly calculateLeveransvirkeSum = (handelssortimentsgrupp: IHandelssortimentsgrupp | IHandelssortiment): number => handelssortimentsgrupp.leveransvirkeKvantitet;

    private readonly calculateKlaraSum = (handelssortimentsgrupp: IHandelssortimentsgrupp | IHandelssortiment): number =>
        handelssortimentsgrupp.klaraEgenSkogKvantitet + handelssortimentsgrupp.klaraVirkeskopKvantitet;

    private tryGetRelated(name: string, relateradeHandelssortiment): string[] {
        if (!relateradeHandelssortiment ||
            relateradeHandelssortiment === null ||
            !relateradeHandelssortiment.length ||
            !relateradeHandelssortiment.filter(r => r.name === name)) {
            return [];
        }

        const relatedEntry = relateradeHandelssortiment.filter(r => r.name === name);
        if (relatedEntry.length > 0) {
            return relatedEntry[0].children;
        }
        return [];
    }

    private readonly isProduktionsmalPlanDetailModeEnabled = (planMode): boolean => planMode === ProduktionsmalPlanMode.detail;

    private readonly getCustomTooltipLabel = (dataSource, rowIndex, labelText, chartMode, planMode, related): string => {
        const relatedFiltered = this.tryGetRelated(labelText, related);

        if (this.isProduktionsmalPlanDetailModeEnabled(planMode)) {
            return this.parseTooltipLabel(dataSource, labelText, relatedFiltered);
        }

        switch (chartMode) {
            case ChartMode.Handelssortimentsgrupper:
                return this.parseTooltipLabel(dataSource, labelText.charAt(0).toLowerCase() + labelText.slice(1), []);
            case ChartMode.Handelssortimentsgrupp:
                // eslint-disable-next-line sonarjs/no-small-switch
                switch (rowIndex) {
                    case 0:
                        return this.parseTooltipLabel(dataSource, labelText.charAt(0).toLowerCase() + labelText.slice(1), relatedFiltered);
                    default:
                        return this.parseTooltipLabel(dataSource, labelText, relatedFiltered);
                }
        }

        throw new Error("Ogiltigt värde: chartMode");
    };

    private readonly getCustomTooltipFooter = (dataSource, rowIndex, chartMode, planMode, selectedHandelssortimentsgrupp, handelssortimentsgrupper): string | string[] => {
        if (this.isProduktionsmalPlanDetailModeEnabled(planMode)) {
            const handelssortimentsgrupp = selectedHandelssortimentsgrupp.handelssortiment[rowIndex];
            return this.getTooltipByTooltipMode(dataSource, handelssortimentsgrupp);
        }

        switch (chartMode) {
            case ChartMode.Handelssortimentsgrupper: {
                const handelssortimentsgrupp = handelssortimentsgrupper[rowIndex];
                return this.getTooltipByTooltipMode(dataSource, handelssortimentsgrupp);
            }
            case ChartMode.Handelssortimentsgrupp:

                switch (rowIndex) {
                    case 0:
                        return this.getTooltipByTooltipMode(dataSource, selectedHandelssortimentsgrupp);
                    case 1:
                        throw new Error("Användaren klickade på en osynlig rad.");
                    default: {
                        const handelssortiment = selectedHandelssortimentsgrupp.handelssortiment[rowIndex - 2];
                        return this.getTooltipByTooltipMode(dataSource, handelssortiment);
                    }
                }
        }

        throw new Error("Ogiltigt värde: chartMode");
    };

    private readonly getCustomLabelColor = (dataSource): string => {
        switch (dataSource) {
            case TooltipMode.Mal:
                return this.colors.mal;
            case TooltipMode.Produktsatt:
                return this.colors.produktsatt;
            case TooltipMode.Periodsatt:
                return this.colors.periodsatt;
            case TooltipMode.Leveransvirke:
                return this.colors.leveransvirke;
            case TooltipMode.Klara:
                return this.colors.klara;
        }

        throw new Error("Ogiltigt värde: dataSource");
    };

    private readonly getTooltipByTooltipMode = (dataSource, dataObject: IHandelssortimentsgrupp | IHandelssortiment) => {
        switch (dataSource) {
            case TooltipMode.Mal:
                return this.renderMal(dataObject);
            case TooltipMode.Produktsatt:
                return this.renderProduktsatt(dataObject);
            case TooltipMode.Periodsatt:
                return this.renderPeriodsatt((dataObject as IHandelssortimentsgrupp));
            case TooltipMode.Leveransvirke:
                return this.renderLeveransvirke(dataObject);
            case TooltipMode.Klara:
                return this.renderKlara(dataObject);
        }

        throw new Error("Ogiltigt värde: dataSource");
    };

    private readonly renderMal = (dataObject: IHandelssortimentsgrupp | IHandelssortiment) => [
        this.$translate.instant("PLANERA_DETAIL_PRODUKTIONSMAL_TOOLTIP_TOTALT_MAL",
            { totalKvantitet: formatNumber(dataObject.malKvantitet, "sv-SE") })
    ];

    private readonly renderProduktsatt = (dataObject: IHandelssortimentsgrupp | IHandelssortiment): string[] => {
        const totalKvantitet = this.calculateProduktsattSum(dataObject);
        return this.renderTooltip(totalKvantitet,
            dataObject.produktsattVirkeskopKvantitet,
            dataObject.produktsattMaskinlagKvantitet,
            dataObject.produktsattManadKvantitet);
    };

    private readonly renderKlara = (dataObject: IHandelssortimentsgrupp | IHandelssortiment): string[] => {
        const totalKvantitet = this.calculateKlaraSum(dataObject);
        return [`${formatNumber(totalKvantitet, "sv-SE")} totalt varav ${formatNumber(dataObject.klaraVirkeskopKvantitet, "sv-SE")} är virkesköp`];
    };

    private readonly renderPeriodsatt = (dataObject: IHandelssortimentsgrupp): string[] => {
        const totalKvantitet = this.calculatePeriodsattSum(dataObject);
        return this.renderTooltip(totalKvantitet,
            dataObject.periodsattVirkeskopKvantitet,
            dataObject.periodsattMaskinlagKvantitet,
            dataObject.periodsattManadKvantitet);
    };

    private readonly renderLeveransvirke = (dataObject: IHandelssortimentsgrupp | IHandelssortiment): string[] => {
        const totalKvantitet = this.calculateLeveransvirkeSum(dataObject);
        return [
            this.$translate.instant("PLANERA_DETAIL_PRODUKTIONSMAL_TOOLTIP_LEVERANSVIRKE",
                { totalKvantitet: formatNumber(totalKvantitet, "sv-SE") })
        ];
    };

    private readonly renderTooltip = (totalKvantitet: number, virkeskopKvantitet: number, maskinlagKvantitet: number, manadKvantitet: number): string[] => [
        this.$translate.instant("PLANERA_DETAIL_PRODUKTIONSMAL_TOOLTIP_TOTALT_PLAN",
            {
                totalKvantitet: formatNumber(totalKvantitet, "sv-SE"),
                virkeskopKvantitet: formatNumber(virkeskopKvantitet, "sv-SE")
            }),
        this.$translate.instant("PLANERA_DETAIL_PRODUKTIONSMAL_TOOLTIP_PA_MASKINLAG",
            { maskinlagKvantitet: formatNumber(maskinlagKvantitet, "sv-SE") }),
        this.$translate.instant("PLANERA_DETAIL_PRODUKTIONSMAL_TOOLTIP_PA_MANAD",
            { manadKvantitet: formatNumber(manadKvantitet, "sv-SE") })
    ];

    private readonly parseTooltipLabel = (dataSource: TooltipMode, labelText: string, relaterade: string[]): string => {
        let prefix: string;
        switch (dataSource) {
            case TooltipMode.Mal:
                prefix = this.$translate.instant("PLANERA_DETAIL_PRODUKTIONSMAL_MAL");
                break;
            case TooltipMode.Produktsatt:
                prefix = this.$translate.instant("PLANERA_DETAIL_PRODUKTIONSMAL_PRODUKTSATT");
                break;
            case TooltipMode.Periodsatt:
                prefix = this.$translate.instant("PLANERA_DETAIL_PRODUKTIONSMAL_PERIODSATT");
                break;
            case TooltipMode.Leveransvirke:
                prefix = this.$translate.instant("PLANERA_DETAIL_PRODUKTIONSMAL_LEVERANSVIRKE");
                break;
            case TooltipMode.Klara:
                prefix = "Klara";
                break;
            default:
                throw new Error("Ogiltigt värde: dataSource");
        }

        let msg = ` ${prefix} ${labelText}`;
        if (relaterade.length > 0) {

            msg += ` (${relaterade.join(", ")})`;
        }

        return msg;
    };

    private readonly createDatasets = (
        malkvantitet: number[],
        produktsattTotalKvantitet: number[],
        periodsattTotalKvantitet: number[],
        leveransvirkeTotalKvantitet: number[],
        klaraTotalKvantitet: number[]): ChartDataset[] =>
        [
            {
                label: this.$translate.instant("PLANERA_DETAIL_PRODUKTIONSMAL_MAL"),
                backgroundColor: this.colors.mal,
                data: malkvantitet,
                barThickness: 20,
                maxBarThickness: 20
            },
            {
                label: "Klara",
                stack: this.stackName,
                backgroundColor: this.colors.klara,
                data: klaraTotalKvantitet,
                barThickness: 20,
                maxBarThickness: 20
            },
            {
                label: this.$translate.instant("PLANERA_DETAIL_PRODUKTIONSMAL_PRODUKTSATT"),
                stack: this.stackName,
                backgroundColor: this.colors.produktsatt,
                data: produktsattTotalKvantitet,
                barThickness: 20,
                maxBarThickness: 20
            },
            {
                label: this.$translate.instant("PLANERA_DETAIL_PRODUKTIONSMAL_PERIODSATT"),
                stack: this.stackName,
                backgroundColor: this.colors.periodsatt,
                data: periodsattTotalKvantitet,
                barThickness: 20,
                maxBarThickness: 20
            },
            {
                label: this.$translate.instant("PLANERA_DETAIL_PRODUKTIONSMAL_LEVERANSVIRKE"),
                stack: this.stackName,
                backgroundColor: this.colors.leveransvirke,
                data: leveransvirkeTotalKvantitet,
                barThickness: 20,
                maxBarThickness: 20
            }
        ];
}

interface IChartData extends ChartData {
    lessThanZeroLabels: string[];
    labels: string[];
    relaterade: INamedLabelList[];
    selectedHandelssortimentgrupp: IHandelssortimentsgrupp;
    handelssortimentgrupper: IHandelssortimentsgrupp[];
}
